import { useEffect } from 'react';

/**
 * Warns before the browser throws away edits nobody has saved.
 *
 * Covers the destructive half of the problem: reloading, closing the tab, and
 * navigating off the site entirely. The browser shows its own dialog — the text
 * is not ours to choose, and every browser ignores a custom message.
 *
 * It does **not** cover navigating to another screen inside the app. Blocking
 * that needs React Router's `useBlocker`, which only works under a data router
 * (`createBrowserRouter`), and this app mounts `<BrowserRouter>`. Until that
 * changes, a page with unsaved edits should also *say* so on screen — which is
 * what the "Unsaved changes" hint beside each Save button is for.
 */
export function useUnsavedChanges(dirty) {
  useEffect(() => {
    if (!dirty) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      // Set for the browsers that still require a non-empty returnValue.
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}

export default useUnsavedChanges;
